import { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Badge, Loading } from "@/components/ui-bits";
import { useAuth } from "@/context/AuthContext";
import { Upload, Loader2, UserPlus, Trash2, Building2, Users, X, Briefcase, Save, Plus, FileText, Search, SlidersHorizontal, Pencil, Phone, Bell, Rocket, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";
import ZooplaPanel from "@/components/ZooplaPanel";

const ROLE_OPTIONS = ["admin", "staff", "landlord", "tenant", "contractor"];

const SETTINGS_TABS = [
  { key: "company", label: "Company", icon: Building2, desc: "Branding, fees & letterhead details" },
  { key: "team", label: "Team & Roles", icon: Users, desc: "Users, access & job assignments" },
  { key: "integrations", label: "Integrations", icon: Rocket, desc: "Go-live status & portal feeds" },
];

export default function Settings() {
  const { company, checkAuth, user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [tab, setTab] = useState("company");
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState(company?.name || "");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "staff", password: "", phone: "" });
  const [userSearch, setUserSearch] = useState("");
  const [manageUser, setManageUser] = useState(null);
  const [notifyUser, setNotifyUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", role: "staff", password: "" });
  const logoRef = useRef(null);

  const loadUsers = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { loadUsers(); }, []);
  useEffect(() => { setName(company?.name || ""); }, [company]);

  const [integrations, setIntegrations] = useState(null);
  useEffect(() => { api.get("/config/integrations").then(({ data }) => setIntegrations(data)).catch(() => setIntegrations(null)); }, []);

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", "company");
      fd.append("entity_id", company.company_id);
      fd.append("bucket", "branding");
      fd.append("file", f);
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await checkAuth();
      toast.success("Logo updated");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (logoRef.current) logoRef.current.value = ""; }
  };

  const saveName = async () => {
    await api.patch("/company", { name });
    await checkAuth();
    toast.success("Company name saved");
  };

  const [mgmtFee, setMgmtFee] = useState("");
  const [maintFee, setMaintFee] = useState("");
  useEffect(() => { setMgmtFee(company?.management_fee_pct ?? 10); setMaintFee(company?.maintenance_fee_pct ?? 0); }, [company]);
  const saveFees = async () => {
    await api.patch("/company", { management_fee_pct: parseFloat(mgmtFee) || 0, maintenance_fee_pct: parseFloat(maintFee) || 0 });
    await checkAuth();
    toast.success("Default fees saved");
  };

  // ---- Company details (letterhead / documents) ----
  const [details, setDetails] = useState({ trading_address: "", email: "", phone: "", website: "", registered_address: "", company_number: "", vat_number: "", bank_account_name: "", bank_sort_code: "" });
  const [savingDetails, setSavingDetails] = useState(false);
  useEffect(() => {
    if (company) setDetails({
      trading_address: company.trading_address || "", email: company.email || "", phone: company.phone || "",
      website: company.website || "",
      registered_address: company.registered_address || "", company_number: company.company_number || "", vat_number: company.vat_number || "",
      bank_account_name: company.bank_account_name || "", bank_sort_code: company.bank_sort_code || "",
    });
  }, [company]);
  const saveDetails = async () => {
    setSavingDetails(true);
    try { await api.patch("/company", details); await checkAuth(); toast.success("Company details saved"); }
    catch { toast.error("Could not save details"); }
    finally { setSavingDetails(false); }
  };

  // ---- Job types & staff assignment ----
  const [jobTypes, setJobTypes] = useState(null);
  const [staff, setStaff] = useState([]);
  const [newJob, setNewJob] = useState("");
  const loadJobTypes = () => api.get("/job-types").then(({ data }) => { setJobTypes(data.job_types); setStaff(data.staff); });
  useEffect(() => { loadJobTypes(); }, []);
  const assignJob = async (job_type, assignee_user_id) => {
    setJobTypes((prev) => prev.map((j) => j.job_type === job_type ? { ...j, assignee_user_id, assignee_name: staff.find((s) => s.user_id === assignee_user_id)?.name || "" } : j));
    await api.patch("/job-types/assign", { job_type, assignee_user_id: assignee_user_id || null });
  };
  const addJobType = async (e) => {
    e.preventDefault();
    const jt = newJob.trim();
    if (!jt) return;
    try { await api.post("/job-types", { job_type: jt }); setNewJob(""); loadJobTypes(); toast.success("Job added"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not add job"); }
  };
  const delJobType = async (job_type) => { await api.delete(`/job-types?job_type=${encodeURIComponent(job_type)}`); loadJobTypes(); };

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User added");
      setOpen(false);
      setForm({ name: "", email: "", role: "staff", password: "", phone: "" });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add user");
    }
  };

  const openEditUser = (u) => { setEditForm({ name: u.name || "", phone: u.phone || "", role: u.role, password: "" }); setEditUser(u); };
  const saveEditUser = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: editForm.name, phone: editForm.phone, role: editForm.role };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${editUser.user_id}`, payload);
      toast.success("User updated");
      setEditUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update user");
    }
  };

  const changeRole = async (u, role) => {
    setUsers((prev) => prev.map((x) => x.user_id === u.user_id ? { ...x, role } : x));
    await api.patch(`/users/${u.user_id}`, { role });
    toast.success(`${u.name} is now ${role}`);
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name}?`)) return;
    await api.delete(`/users/${u.user_id}`);
    toast.success("User removed");
    loadUsers();
  };

  const logoUrl = company?.logo_file_id ? fileUrl(company.logo_file_id) : null;

  return (
    <Layout title="Settings" subtitle="Company">
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        <nav aria-label="Settings sections" className="lg:w-60 shrink-0">
          <div className="lg:sticky lg:top-6 flex lg:flex-col gap-1.5 overflow-x-auto pb-1 lg:pb-0 -mx-1 px-1">
            {SETTINGS_TABS.map((t) => {
              const Icon = t.icon; const on = tab === t.key;
              return (
                <button key={t.key} type="button" data-testid={`settings-tab-${t.key}`} onClick={() => setTab(t.key)}
                  className={`group flex items-start gap-3 rounded-xl px-3.5 py-3 text-left whitespace-nowrap transition-all ${on ? "bg-blue-700 text-white shadow-sm shadow-blue-700/20" : "text-slate-600 hover:bg-slate-100"}`}>
                  <Icon size={17} className={`mt-0.5 shrink-0 ${on ? "text-white" : "text-blue-700"}`} />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold leading-tight">{t.label}</span>
                    <span className={`hidden lg:block text-xs leading-tight mt-0.5 ${on ? "text-blue-100" : "text-slate-400"}`}>{t.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
        <div className="flex-1 min-w-0 space-y-6">
          {tab === "company" && (
          <Card className="p-6" data-testid="branding-card">
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4"><Building2 size={18} className="text-blue-700" /> Branding</h3>
          <div className="flex items-center justify-center h-28 bg-slate-50 rounded-xl border border-slate-200 mb-4 overflow-hidden">
            {logoUrl ? <img src={logoUrl} alt="Company logo" className="max-h-24 max-w-full object-contain" data-testid="company-logo-preview" />
              : <span className="text-sm text-slate-400">No logo uploaded</span>}
          </div>
          <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="logo-file-input" onChange={uploadLogo} />
          <button data-testid="upload-logo-button" onClick={() => logoRef.current?.click()} disabled={uploading}
            className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload Logo
          </button>
          <p className="text-xs text-slate-400 mt-2">Appears in the app header and on your documents.</p>

          <label className="block text-sm font-medium text-slate-700 mb-1 mt-6">Company Name</label>
          <div className="flex gap-2">
            <input data-testid="company-name-input" value={name} onChange={(e) => setName(e.target.value)} className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button data-testid="save-company-name" onClick={saveName} className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-3 py-2 text-sm font-medium">Save</button>
          </div>

          <div className="mt-6 pt-5 border-t border-slate-100">
            <h4 className="text-sm font-semibold text-slate-700 mb-1">Default fees</h4>
            <p className="text-xs text-slate-400 mb-3">Applied to every property unless overridden per-property in Landlords.</p>
            <div className="flex items-end gap-2">
              <label className="text-xs text-slate-500">Management %
                <input data-testid="default-mgmt-fee" type="number" step="0.5" min="0" value={mgmtFee} onChange={(e) => setMgmtFee(e.target.value)}
                  className="block w-24 mt-0.5 rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </label>
              <label className="text-xs text-slate-500">Maintenance %
                <input data-testid="default-maint-fee" type="number" step="0.5" min="0" value={maintFee} onChange={(e) => setMaintFee(e.target.value)}
                  className="block w-24 mt-0.5 rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </label>
              <button data-testid="save-default-fees" onClick={saveFees} className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2 text-sm font-medium">Save</button>
            </div>
          </div>
        </Card>
          )}

          {tab === "team" && (
          <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><Users size={18} className="text-blue-700" /> Users & Staff</h3>
            <button data-testid="add-user-button" onClick={() => setOpen(true)} className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors">
              <UserPlus size={15} /> Add User
            </button>
          </div>
          <div className="relative mb-4">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="user-search" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search users by name or email…"
              className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          {!users ? <Loading /> : (
            <div className="divide-y divide-slate-100">
              {users.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(userSearch.toLowerCase())).map((u) => (
                <div key={u.user_id} className="flex items-center gap-4 py-3" data-testid={`user-row-${u.email}`}>
                  <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center shrink-0">
                    {u.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{u.name} {u.user_id === me.user_id && <span className="text-xs text-slate-400">(you)</span>}</p>
                    <p className="text-xs text-slate-400 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p>
                  </div>
                  {u.role === "landlord" && (
                    <button data-testid={`manage-user-${u.email}`} onClick={() => setManageUser(u)} title="Manage access"
                      className="text-slate-500 hover:text-blue-700 border border-slate-200 hover:border-blue-300 rounded-md px-2 py-1.5 text-xs font-medium flex items-center gap-1.5"><SlidersHorizontal size={13} /> Manage</button>
                  )}
                  {(u.role === "staff" || u.role === "admin" || u.role === "contractor") && (
                    <button data-testid={`notify-user-${u.email}`} onClick={() => setNotifyUser(u)} title="Notification preferences"
                      className="text-slate-500 hover:text-blue-700 border border-slate-200 hover:border-blue-300 rounded-md px-2 py-1.5 text-xs font-medium flex items-center gap-1.5"><Bell size={13} /> Manage</button>
                  )}
                  <button data-testid={`edit-user-${u.email}`} onClick={() => openEditUser(u)} title="Edit user"
                    className="text-slate-400 hover:text-blue-700 transition-colors"><Pencil size={15} /></button>
                  <select data-testid={`role-select-${u.email}`} value={u.role} onChange={(e) => changeRole(u, e.target.value)} disabled={u.user_id === me.user_id}
                    className="rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:opacity-50 capitalize">
                    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button data-testid={`delete-user-${u.email}`} onClick={() => removeUser(u)} disabled={u.user_id === me.user_id}
                    className="text-slate-400 hover:text-red-500 disabled:opacity-30 transition-colors"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </Card>
          )}

          {tab === "integrations" && (
          <Card className="p-6" data-testid="go-live-card">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2"><Rocket size={18} className="text-blue-700" /><h3 className="font-display text-lg font-semibold text-slate-900">Go Live Checklist</h3></div>
          {integrations && <span data-testid="go-live-summary" className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">{integrations.summary.ready}/{integrations.summary.total} ready</span>}
        </div>
        <p className="text-xs text-slate-400 mb-5">See at a glance which integrations are live vs still in test mode, and what's needed to go fully live.</p>
        {!integrations ? <Loading /> : (
          <div className="space-y-2.5">
            {integrations.integrations.map((it) => <IntegrationRow key={it.key} it={it} />)}
          </div>
        )}
        </Card>
          )}

          {tab === "company" && (
          <Card className="p-6" data-testid="company-details-card">
        <div className="flex items-center gap-2 mb-1"><FileText size={18} className="text-blue-700" /><h3 className="font-display text-lg font-semibold text-slate-900">Company Details</h3></div>
        <p className="text-xs text-slate-400 mb-5">Used on document letterheads (e.g. Section 13 rent increase notices) and statements.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><label className="block text-sm font-medium text-slate-700 mb-1">Trading address</label>
            <textarea data-testid="company-trading-address" rows={2} value={details.trading_address} onChange={(e) => setDetails({ ...details, trading_address: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input data-testid="company-email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input data-testid="company-phone" value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
            <input data-testid="company-website" value={details.website} onChange={(e) => setDetails({ ...details, website: e.target.value })} placeholder="www.yourcompany.co.uk" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
          <div className="md:col-span-2"><label className="block text-sm font-medium text-slate-700 mb-1">Registered address</label>
            <textarea data-testid="company-registered-address" rows={2} value={details.registered_address} onChange={(e) => setDetails({ ...details, registered_address: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Company registration number</label>
            <input data-testid="company-number" value={details.company_number} onChange={(e) => setDetails({ ...details, company_number: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">VAT number</label>
            <input data-testid="company-vat" value={details.vat_number} onChange={(e) => setDetails({ ...details, vat_number: e.target.value })} placeholder="Leave blank if not VAT registered" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Bank account name</label>
            <input data-testid="company-bank-name" value={details.bank_account_name} onChange={(e) => setDetails({ ...details, bank_account_name: e.target.value })} placeholder="Shown on statement payment details" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Account no. / Sort code</label>
            <input data-testid="company-bank-sort" value={details.bank_sort_code} onChange={(e) => setDetails({ ...details, bank_sort_code: e.target.value })} placeholder="12345678 · 05-80" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
        </div>
        <button data-testid="save-company-details" onClick={saveDetails} disabled={savingDetails} className="mt-4 bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-60">
          {savingDetails ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Details
        </button>
        </Card>
          )}

          {tab === "team" && (
          <Card className="p-6" data-testid="job-roles-card">
        <div className="flex items-center gap-2 mb-1"><Briefcase size={18} className="text-blue-700" /><h3 className="font-display text-lg font-semibold text-slate-900">Job Roles & Staff Assignment</h3></div>
        <p className="text-xs text-slate-400 mb-4">Assign a staff member to each type of job. New tasks of that type are auto-assigned to them (you can still override).</p>
        <form onSubmit={addJobType} className="flex gap-2 mb-5 max-w-md">
          <input data-testid="new-job-type-input" value={newJob} onChange={(e) => setNewJob(e.target.value)} placeholder="Add a custom job type…" className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          <button type="submit" data-testid="add-job-type-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-1.5"><Plus size={15} /> Add</button>
        </form>
        {!jobTypes ? <Loading /> : (
          <div className="max-h-[460px] overflow-y-auto pr-1 space-y-4">
            {Object.entries(jobTypes.reduce((acc, j) => { (acc[j.category || "Other"] = acc[j.category || "Other"] || []).push(j); return acc; }, {})).map(([cat, jobs]) => (
              <div key={cat} data-testid={`job-cat-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
                <p className="text-[11px] font-bold uppercase tracking-wider text-blue-700 mb-1 sticky top-0 bg-white py-1">{cat}</p>
                <div className="divide-y divide-slate-100">
                  {jobs.map((j, idx) => (
                    <div key={`${cat}-${j.job_type}-${idx}`} className="flex items-center gap-3 py-2" data-testid={`job-row-${j.job_type}`}>
                      <span className="flex-1 text-sm text-slate-700">{j.job_type}{j.is_custom && <span className="ml-2 text-[11px] text-blue-600">custom</span>}</span>
                      <select data-testid={`job-assign-${j.job_type}`} value={j.assignee_user_id || ""} onChange={(e) => assignJob(j.job_type, e.target.value)}
                        className="rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 min-w-[160px]">
                        <option value="">— Unassigned —</option>
                        {staff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
                      </select>
                      {j.is_custom && <button data-testid={`del-job-${j.job_type}`} onClick={() => delJobType(j.job_type)} className="text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        </Card>
          )}

          {tab === "integrations" && (
          <Card className="p-6" data-testid="zoopla-card">
            <ZooplaPanel />
          </Card>
          )}
        </div>
      </div>
      {manageUser && <ManageUserModal u={manageUser} onClose={() => setManageUser(null)} />}
      {notifyUser && <NotifyPrefsModal u={notifyUser} onClose={() => setNotifyUser(null)} />}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">Add User</h3>
              <button type="button" data-testid="close-add-user" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={addUser} className="space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                <input data-testid="new-user-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input data-testid="new-user-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Phone number</label>
                <input data-testid="new-user-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="e.g. 07700 900123" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <select data-testid="new-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 capitalize">
                    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                  <input data-testid="new-user-password" required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              </div>
              <button data-testid="submit-user-button" type="submit" className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium transition-colors">Create User</button>
            </form>
          </div>
        </div>
      )}

      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setEditUser(null)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">Edit User</h3>
              <button type="button" data-testid="close-edit-user" onClick={() => setEditUser(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={saveEditUser} className="space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                <input data-testid="edit-user-name" required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input data-testid="edit-user-email" value={editUser.email} readOnly className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Phone number</label>
                <input data-testid="edit-user-phone" type="tel" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="e.g. 07700 900123" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <select data-testid="edit-user-role" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} disabled={editUser.user_id === me.user_id} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 capitalize disabled:opacity-50">
                    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">New password</label>
                  <input data-testid="edit-user-password" type="text" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Leave blank to keep" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              </div>
              <button data-testid="save-edit-user-button" type="submit" className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium transition-colors">Save changes</button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}


const STATUS_META = {
  live: { label: "Live", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2, dot: "text-emerald-500" },
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2, dot: "text-emerald-500" },
  configured: { label: "Configured", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2, dot: "text-emerald-500" },
  test: { label: "Test mode", cls: "bg-amber-100 text-amber-700", Icon: AlertTriangle, dot: "text-amber-500" },
  not_configured: { label: "Not set", cls: "bg-red-100 text-red-700", Icon: XCircle, dot: "text-red-500" },
};

function IntegrationRow({ it }) {
  const m = STATUS_META[it.status] || STATUS_META.not_configured;
  const Icon = m.Icon;
  return (
    <div data-testid={`integration-${it.key}`} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
      <Icon size={18} className={`mt-0.5 shrink-0 ${m.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-slate-900">{it.name}</p>
          <span data-testid={`integration-status-${it.key}`} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">{it.powers}</p>
        {it.detail && <p className="text-xs text-slate-400 mt-0.5">{it.detail}</p>}
        {it.go_live && <p className="text-xs text-blue-600 mt-1"><span className="font-medium">To go live:</span> {it.go_live}</p>}
      </div>
    </div>
  );
}


function Toggle({ label, desc, checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className="w-full flex items-center justify-between gap-3 py-2.5 text-left">
      <div>
        <p className="text-sm font-medium text-slate-800">{label}</p>
        {desc && <p className="text-xs text-slate-400">{desc}</p>}
      </div>
      <span className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-slate-300"}`}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
}

function ManageUserModal({ u, onClose }) {
  const [acc, setAcc] = useState(null);
  const [allProps, setAllProps] = useState([]);
  const [extra, setExtra] = useState([]);
  const [flags, setFlags] = useState({ can_see_schedule: true, can_see_messages: true, can_see_accounts: true });
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/users/${u.user_id}/access`).then(({ data }) => {
      setAcc(data);
      setExtra(data.extra_property_ids || []);
      setFlags({ can_see_schedule: data.can_see_schedule, can_see_messages: data.can_see_messages, can_see_accounts: data.can_see_accounts });
    }).catch(() => toast.error("Could not load access"));
    api.get("/properties").then(({ data }) => setAllProps(data)).catch(() => {});
  }, [u.user_id]);

  const ownedIds = new Set((acc?.owned_properties || []).map((p) => p.property_id));
  const toggleExtra = (pid) => setExtra((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${u.user_id}/access`, { extra_property_ids: extra, ...flags });
      toast.success("Access updated");
      onClose();
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  const selectable = allProps.filter((p) => !ownedIds.has(p.property_id) &&
    `${p.reference} ${p.address}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900">Manage {u.name}</h3>
            <p className="text-xs text-slate-400">Landlord access & visibility</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        {!acc ? <div className="p-8"><Loading /></div> : (
          <div className="p-5 space-y-5">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Can see</p>
              <div className="rounded-xl border border-slate-200 px-3 divide-y divide-slate-100">
                <Toggle label="Work schedule" desc="Refurbishment & project progress" checked={flags.can_see_schedule} onChange={(v) => setFlags({ ...flags, can_see_schedule: v })} testid="toggle-schedule" />
                <Toggle label="Messages" desc="Message thread with the admin team" checked={flags.can_see_messages} onChange={(v) => setFlags({ ...flags, can_see_messages: v })} testid="toggle-messages" />
                <Toggle label="Accounts & statements" desc="Financials, transactions & statement PDFs" checked={flags.can_see_accounts} onChange={(v) => setFlags({ ...flags, can_see_accounts: v })} testid="toggle-accounts" />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Properties they can see</p>
              {(acc.owned_properties || []).length > 0 && (
                <div className="mb-2">
                  <p className="text-[11px] text-slate-400 mb-1">Owned (always visible)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {acc.owned_properties.map((p) => (
                      <span key={p.property_id} className="text-xs rounded-full bg-slate-100 text-slate-600 px-2.5 py-1">{p.address || p.reference}</span>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-slate-400 mb-1">Additional properties</p>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input data-testid="manage-prop-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search properties…" className="w-full rounded-lg border border-slate-300 pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div className="max-h-44 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                {selectable.length === 0 ? <p className="text-xs text-slate-400 p-3">No other properties.</p> : selectable.map((p) => (
                  <label key={p.property_id} data-testid={`extra-prop-${p.reference}`} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={extra.includes(p.property_id)} onChange={() => toggleExtra(p.property_id)} className="accent-blue-600" />
                    <span className="text-sm text-slate-700">{p.address}</span>
                  </label>
                ))}
              </div>
            </div>

            <button data-testid="save-access" onClick={save} disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-lg py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save access
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


const NOTIFY_LABELS = { tasks: "Tasks", lettings: "Lettings", sales: "Sales", maintenance: "Maintenance", projects: "Projects", finance: "Finance", messages: "Messages", jobs: "Jobs" };
const NOTIFY_DESC = {
  tasks: "A task is assigned to them",
  lettings: "New lettings activity",
  sales: "New sales activity",
  maintenance: "New maintenance & work orders",
  projects: "New refurbishment projects",
  finance: "Rent, arrears & finance alerts",
  messages: "A new message arrives",
  jobs: "A new job is assigned to them",
};

function PrefSwitch({ on, onClick, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} role="switch" aria-checked={on}
      className={`relative w-10 h-6 rounded-full transition-colors shrink-0 ${on ? "bg-blue-600" : "bg-slate-200"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${on ? "translate-x-4" : ""}`} />
    </button>
  );
}

function NotifyPrefsModal({ u, onClose }) {
  const [data, setData] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/users/${u.user_id}/notify-prefs`)
      .then(({ data }) => { setData(data); setPrefs(data.prefs); })
      .catch(() => toast.error("Could not load preferences"));
  }, [u.user_id]);

  const toggle = (ch, cat) => setPrefs((p) => ({ ...p, [ch]: { ...p[ch], [cat]: !p[ch][cat] } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${u.user_id}/notify-prefs`, { prefs });
      toast.success("Notification preferences saved");
      onClose();
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="notify-prefs-modal" className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><Bell size={17} className="text-blue-700" /> Notifications · {u.name}</h3>
            <p className="text-xs text-slate-400">Choose which alerts they receive by email and text message</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        {!prefs ? <div className="p-8"><Loading /></div> : (
          <div className="p-5">
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-5 items-center px-3 pb-2 mb-1 border-b border-slate-100">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Notify me about</span>
              <span className="text-xs font-semibold text-slate-500 w-10 text-center">Email</span>
              <span className="text-xs font-semibold text-slate-500 w-10 text-center">Text</span>
            </div>
            <div className="divide-y divide-slate-100">
              {data.categories.map((cat) => (
                <div key={cat} className="grid grid-cols-[1fr_auto_auto] gap-x-5 items-center px-3 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800">{NOTIFY_LABELS[cat] || cat}</p>
                    <p className="text-xs text-slate-400">{NOTIFY_DESC[cat] || ""}</p>
                  </div>
                  <div className="w-10 flex justify-center">
                    <PrefSwitch on={!!prefs.email[cat]} onClick={() => toggle("email", cat)} testid={`notify-email-${cat}`} />
                  </div>
                  <div className="w-10 flex justify-center">
                    <PrefSwitch on={!!prefs.sms[cat]} onClick={() => toggle("sms", cat)} testid={`notify-sms-${cat}`} />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-3 px-1">Text messages are sent by SMS and may incur a small cost. A mobile number must be set on the user for texts to send.</p>
            <button data-testid="save-notify-prefs" onClick={save} disabled={busy} className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white rounded-lg py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save preferences
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
